// 레이드 보스 '잿불 비룡' 렌더: 전용 리깅 GLB(public/assets/bosses/boss_wyvern.glb, tools/blender/wyvern_build.py)를 AnimationMixer로 움직인다.
// 클립 시간은 게임 상태·stateTick에서 바로 정하고(준비 동작이 경고 장판과 같은 틱에 끝난다), 반복 클립(대기·걷기·비행)만 프레임 시간으로 흐른다.
// 클립끼리는 무게를 지수 감쇠로 넘긴다(무게 합은 늘 1). 공중 높이·몸 방향·미끄러지듯 따라가기는 코드가 정한다.
// GLB를 읽는 동안·읽기 실패·?wyvernproc 비교에는 절차 대체판(wyvernProc.ts)을 그린다. 프레임 루프에서 새 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { abs, float, floor, fract, fwidth, materialEmissive, max, mix, mx_noise_float, mx_worley_noise_vec2, normalView, positionGeometry, positionViewDirection, positionWorld, screenCoordinate, sin, smoothstep, texture, uniform, uv, vec3 } from 'three/tsl';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { WYVERN, WyvernPattern } from '../../shared/content/raid';
import { BossState, type GameView } from '../../shared/types';
import type { GameClocks } from '../clock';
import { hullMaterial, RimControl } from '../materials';
/** 머리 뼈 배율(뿔·턱 포함). 몸통·날개 크기 예산(wyvern.test)에는 영향이 없다 */
export const WYVERN_HEAD_SCALE = 1.32;
import { angleDelta, clamp01, damp, easeOut, phaseOf } from '../util';
import { resolveAssetUrl } from '../../pwa/assetMap';
import { WyvernProcVisual } from './wyvernProc';
import { BOSS_LANE_DEFAULT, setCutawayBossLane } from '../env/cut';

/** 비행 높이(m, 공중 1.0일 때 몸 높이) */
export const FLY_H = WYVERN.flight.height + 1.5;
/** GLB 모델 배율(모델은 실제 크기 m: 코끝~꼬리 끝 14m, 날개 편 폭 약 15m, 뿔 끝 높이 약 9.4m — C8-3 Tripo 모델).
 * L11에서 예전 스크립트 모델(높이 약 6.5m)을 1.4배로 키웠다. C8-3 모델은 목을 세워 키가 커서 1.4배면 데스크톱 A·B 장면에서
 * 머리가 화면 위로 잘렸다. 1.05배는 블라인드 비평에서 '아레나 대비 작고 압도감이 없음'(r2), 1.25배는 A·B에서 다시 머리가 잘림(r3) → 1.15배. 판정 반경(WYVERN.radius)·경고는 그대로 */
export const MODEL_SCALE = 1.15;
/** 2페이즈 전환 포효(초): 쫓기·대기 중이면 포효 클립을 덮어 씌운다 */
const PHASE_ROAR_S = 1.7;
/** 보스 시선 통로 반폭(m): 반쯤 편 날개 폭의 반 + 기둥 반경. C8-2: 기본 5.5m는 폰 화면에서 옆 기둥이 비룡을 반쯤 가렸다.
 * C8-3 Tripo 모델(날개 편 폭 약 15m × 배율 1.15 ≈ 17m, 반쯤 편 폭 약 12m)도 같은 8m로 덮인다 */
export const WYVERN_LANE = 8;
/** C8-6 재질: 텍스처 넓은 발광 섬(가슴·어깨 판) 세기(예전 3.2), 몸 전체 용암 맥 세기·셀 주파수(1/m, 비늘판 한 칸 약 0.9m), 노멀맵 깊이 배율 */
export const CRACK_W = 2.4;
export const VEIN_W = 2.1;
/** C8-6 r4 잔균열 세기(큰 균열 사이 가는 주홍 선) */
export const FINE_W = 0.9;
export const VEIN_FREQ = 1.4;
export const WYVERN_NORMAL_BOOST = 1.5;
/** 피격 번쩍임 길이(틱) */
const HIT_FLASH_TICKS = 5;
export const WYVERN_GLB = 'assets/bosses/boss_wyvern.glb';

/** GLB 클립 이름(wyvern_clips.py CLIPS와 같다) */
export const WYVERN_CLIPS = ['idle', 'walk', 'bite', 'tail', 'spit', 'charge', 'roar', 'takeoff', 'fly', 'breath_air', 'land', 'hit', 'stagger', 'death'] as const;
export type WyvernClip = typeof WYVERN_CLIPS[number];
const CLIP_INDEX: Record<WyvernClip, number> = Object.fromEntries(WYVERN_CLIPS.map((c, i) => [c, i])) as Record<WyvernClip, number>;
/** 반복 클립: 시간은 프레임마다 흐른다(나머지는 상태 틱에서 정한다) */
const LOOP: ReadonlySet<WyvernClip> = new Set(['idle', 'walk', 'fly']);
/** 클립 안 준비 구간 길이(초): 게임 준비 틱을 이 길이로 늘이거나 줄여 맞춘다 */
const WINDUP_S = { bite: 1.0, tail: 1.1, spit: 1.4, charge: 1.2, land: 1.4 } as const;
/** 걷기 클립이 만들어진 이동 속도(m/s) */
const WALK_SPEED = WYVERN.speed;
/** 등장: 이 비율까지 하늘에서 내려오고(착지 클립 0~1.4초), 착지 충격 21틱 뒤 포효한다 */
const INTRO_DOWN = 0.5;

export interface ClipPick { clip: WyvernClip; /** 클립 시간(초). 반복 클립은 -1(흐르는 시간 사용) */ time: number }
export interface BreathPhase { aim: number; fire: number }

/** 브레스(공중) 준비·분사 정도를 out에 쓴다(프레임마다 부르므로 할당 없음). stepFlight 2단계는 stateTick 0부터 브레스 줄마다 breathGap 간격으로 경고를 깐다 */
export function breathPhase(t: number, out: BreathPhase): BreathPhase {
  const F = WYVERN.flight;
  let fire = 0, aim = 0;
  for (let k = 0; k < F.breaths; k++) {
    const s0 = k * F.breathGap + F.breathWindup - 34, s1 = k * F.breathGap + F.breathWindup + 10;
    if (t >= s0 - 30 && t < s1) aim = Math.max(aim, clamp01((t - (s0 - 30)) / 30));
    if (t >= s0 && t < s1) fire = Math.max(fire, Math.min(1, (t - s0) / 6, (s1 - t) / 8));
  }
  out.aim = aim; out.fire = fire;
  return out;
}

const setPick = (out: ClipPick, clip: WyvernClip, time: number): ClipPick => { out.clip = clip; out.time = time; return out; };
const scratchBreath: BreathPhase = { aim: 0, fire: 0 };

/**
 * 게임 상태 → 클립과 클립 시간을 out에 쓴다(할당 없음). t = 상태 틱(보간), lastPat = 이번 공격 패턴(친 뒤 상태에서도 기억), moving = 걷는 중.
 * 표는 PR·docs/RAID-PRD.md 9절에 그대로 적는다.
 */
export function pickClip(out: ClipPick, state: number, t: number, lastPat: number, air: number, moving: boolean): ClipPick {
  const W = WYVERN;
  switch (state) {
    case BossState.Intro: {
      const down = W.intro * INTRO_DOWN;
      if (t < down) return setPick(out, 'land', (t / down) * WINDUP_S.land);
      if (t < down + 21) return setPick(out, 'land', WINDUP_S.land + (t - down) / 60);
      return setPick(out, 'roar', (t - down - 21) / 60);
    }
    case BossState.Chase: return setPick(out, moving ? 'walk' : 'idle', -1);
    case BossState.SlamWindup: return setPick(out, 'bite', (t / W.bite.windup) * WINDUP_S.bite);
    case BossState.SweepWindup:
      return lastPat === WyvernPattern.charge
        ? setPick(out, 'charge', (t / W.charge.windup) * WINDUP_S.charge)
        : setPick(out, 'tail', (t / W.tail.windup) * WINDUP_S.tail);
    case BossState.Slam:
      if (lastPat === WyvernPattern.charge) return setPick(out, 'charge', WINDUP_S.charge + t / 60);
      if (lastPat === WyvernPattern.land) return setPick(out, 'land', WINDUP_S.land + t / 60);
      return setPick(out, 'bite', WINDUP_S.bite + t / 60);
    case BossState.Sweep:
      return lastPat === WyvernPattern.pools
        ? setPick(out, 'spit', WINDUP_S.spit + t / 60)
        : setPick(out, 'tail', WINDUP_S.tail + t / 60);
    case BossState.Cast: {
      if (lastPat === WyvernPattern.takeoff) return setPick(out, 'takeoff', t / 60);
      if (lastPat === WyvernPattern.breath && air > 0) {
        const { aim, fire } = breathPhase(t, scratchBreath);
        return aim > 0.01 || fire > 0.01 ? setPick(out, 'breath_air', 0.45 * aim + 0.55 * fire) : setPick(out, 'fly', -1);
      }
      if (lastPat === WyvernPattern.land) return setPick(out, 'land', (t / W.flight.landWindup) * WINDUP_S.land);
      if (air > 0) return setPick(out, 'fly', -1);
      return setPick(out, 'spit', (t / W.pools.windup) * WINDUP_S.spit);
    }
    case BossState.Stagger: return setPick(out, t < 24 ? 'hit' : 'stagger', t / 60);
    case BossState.Dying: return setPick(out, 'death', t / 60);
    default: return setPick(out, 'idle', -1);
  }
}

const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
const base = (): string => import.meta.env?.BASE_URL ?? './';

export class WyvernVisual {
  readonly root = new THREE.Group();
  readonly rim = new RimControl(0xffb070, 0.32);
  x = 0; z = 0;
  /** 공중 높이(m, 그림자·카메라용) */
  height = 0;
  /** 임시 브레스 원뿔(입 뼈에 붙음)을 그릴지. VFX 레인이 raidFx 쪽 브레스로 바꾸면 끈다 */
  breathCone = true;
  /** 전용 GLB를 쓰는 중인지(QA·대체 확인) */
  dedicated = false;
  /** GLB 읽기가 끝났는지(성공·실패 모두) */
  settled = false;

  private readonly proc: WyvernProcVisual;
  private readonly model = new THREE.Group();
  private mesh: THREE.SkinnedMesh | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private readonly actions: (THREE.AnimationAction | null)[] = WYVERN_CLIPS.map(() => null);
  private readonly weights = new Float32Array(WYVERN_CLIPS.length);
  private readonly loopTime = new Float32Array(WYVERN_CLIPS.length);
  private mouthBone: THREE.Object3D | null = null;
  private headBone: THREE.Object3D | null = null;
  private readonly mouthRig = new THREE.Group();
  private readonly flame: THREE.Mesh;
  private readonly throat: THREE.Mesh;
  private readonly mats: THREE.Material[] = [];
  private readonly geos: THREE.BufferGeometry[] = [];
  private readonly disposables: THREE.Texture[] = [];
  private skinMat: THREE.MeshStandardNodeMaterial | null = null;
  private readonly hullMat = hullMaterial(0.05);
  private readonly uGlow = uniform(0.85);
  private readonly uGlowTint = uniform(new THREE.Color(1, 1, 1));
  private readonly uThroat = uniform(0);
  private readonly uTime = uniform(0);
  private readonly uFlame = uniform(0);
  private readonly fade = uniform(0);
  /** 피격 번쩍임 0..1(몸 가장자리 위주의 따뜻한 흰빛) */
  private readonly uHit = uniform(0);
  /** 2페이즈 전환 포효 경과(초, 음수 = 없음)·발광 솟음 */
  private roarT = -1;
  private surge = 0;
  private wasPhase2 = false;
  private fadeV = 0;
  private lastPat = -1;
  private lastState = -1;
  private sx = 0; private sz = 0; private yaw = 0;
  private h = 0; private glow = 0.85; private throatV = 0; private flameV = 0;
  private inited = false;
  private readonly tmpA = new THREE.Vector3();
  private readonly tmpB = new THREE.Vector3();
  /** 프레임마다 다시 쓰는 결과 기록(할당 없음) */
  readonly pick: ClipPick = { clip: 'idle', time: -1 };
  private readonly breath: BreathPhase = { aim: 0, fire: 0 };

  /** 노멀맵을 쓸지(품질 low에서는 끈다: 텍스처 1장·접선 계산을 아낀다, C8-5 리뷰 P2) */
  private readonly useNormalMap: boolean;
  /** 절차 용암 맥·판 거칠기(C8-6). 기본은 노멀맵과 같이 켜고 끈다(품질 low에서 셀 잡음 셰이더를 아낀다) */
  private readonly useDetail: boolean;
  constructor(opts: { load?: boolean; normalMap?: boolean; detail?: boolean } = {}) {
    this.useNormalMap = opts.normalMap ?? true;
    this.useDetail = opts.detail ?? this.useNormalMap;
    this.proc = new WyvernProcVisual();
    this.root.add(this.proc.root);
    this.model.visible = false;
    this.model.scale.setScalar(MODEL_SCALE);
    this.root.add(this.model);
    this.root.visible = false;
    // 목구멍 빛·브레스 원뿔(입 뼈 기준 +Z가 입 방향이 되게 mouthRig를 매 프레임 돌린다)
    const throatMat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    throatMat.colorNode = vec3(2.6, 1.3, 0.3).mul(this.uThroat);
    const flameMat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const fv = uv();
    const streak = abs(fract(fv.y.mul(3).sub(this.uTime.mul(2.4)).add(sin(fv.x.mul(18.85)).mul(0.12))).sub(0.5)).mul(2);
    const along = smoothstep(0, 0.12, float(1).sub(fv.y)).mul(smoothstep(0, 0.5, fv.y));
    flameMat.colorNode = mix(vec3(2.0, 0.6, 0.08), vec3(2.2, 1.5, 0.5), fv.y);
    flameMat.opacityNode = along.mul(float(0.55).add(streak.mul(0.45))).mul(this.uFlame);
    this.mats.push(throatMat, flameMat, this.hullMat);
    const throatGeo = new THREE.SphereGeometry(0.34, 10, 8);
    this.throat = new THREE.Mesh(throatGeo, throatMat);
    this.throat.position.set(0, 0.05, -0.55);
    this.throat.renderOrder = 5; this.throat.visible = false; this.throat.frustumCulled = false;
    const flameGeo = new THREE.CylinderGeometry(0.9, 0.12, 1, 10, 1, true);
    flameGeo.rotateX(-Math.PI / 2); flameGeo.translate(0, 0, 0.5);
    this.flame = new THREE.Mesh(flameGeo, flameMat);
    this.flame.position.set(0, -0.05, 0.05);
    this.flame.renderOrder = 6; this.flame.visible = false; this.flame.frustumCulled = false;
    this.geos.push(throatGeo, flameGeo);
    this.mouthRig.add(this.throat, this.flame);
    const want = opts.load ?? (typeof document !== 'undefined' && !(typeof location !== 'undefined' && new URLSearchParams(location.search).has('wyvernproc')));
    if (want) void this.load();
    else this.settled = true;
  }

  /** GLB를 내려받아 붙인다(실패하면 절차 대체판을 계속 쓴다) */
  async load(url = resolveAssetUrl(base() + WYVERN_GLB)): Promise<void> {
    try {
      const gltf = await loader.loadAsync(url);
      this.attach(gltf.scene, gltf.animations);
    } catch (err) {
      console.warn('비룡 GLB를 읽지 못함(절차 모델로 대신한다)', err);
    } finally {
      this.settled = true;
    }
  }

  /** 읽은 GLB 장면을 붙인다(테스트는 직접 파싱해 넘긴다) */
  attach(scene: THREE.Object3D, clips: readonly THREE.AnimationClip[]): void {
    let skinned: THREE.SkinnedMesh | null = null;
    scene.traverse((o) => { if (!skinned && (o as THREE.SkinnedMesh).isSkinnedMesh) skinned = o as THREE.SkinnedMesh; });
    const sm = skinned as THREE.SkinnedMesh | null;
    if (!sm) throw new Error('비룡 GLB에 스킨 메시가 없다');
    const srcMat = sm.material as THREE.MeshStandardMaterial;
    const map = srcMat.map ?? null;
    const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.82, metalness: 0.04 });
    m.emissive = new THREE.Color(0x000000);
    // C8-4: Tripo 원본(약 200만 삼각형)의 비늘판 홈·균열 골을 구운 노멀맵(wyvern_tripo.py bake_normal). 감면 메시가 매끈한 플라스틱으로 읽히던 것(블라인드 P1)
    const normalMap = this.useNormalMap ? srcMat.normalMap ?? null : null;
    if (!normalMap && srcMat.normalMap) srcMat.normalMap.dispose();
    // C8-6(블라인드 P1 '흑요석 표면이 매끈'): 비늘판 골을 조금 더 깊게
    if (normalMap) { m.normalMap = normalMap; m.normalScale.copy(srcMat.normalScale).multiplyScalar(WYVERN_NORMAL_BOOST); }
    const ndv = float(1).sub(normalView.dot(positionViewDirection).clamp(0, 1));
    const fres = ndv.pow(3);
    // 텍스처 알파 = 발광 마스크(용암 균열·눈·입 안·막 핏줄). 발광 = 칠한 색 × 마스크 × 세기(블룸 문턱 위)
    let glowNode: THREE.Node<'vec3'> = vec3(0, 0, 0);
    if (map) {
      const tex = texture(map);
      const pg = positionGeometry; // 쉼 자세 좌표(m, 애니메이션에 흔들리지 않는다)
      // C8-6 몸 전체 용암 맥(블라인드 P1 '균열 발광이 가슴에만 몰림'): 텍스처 마스크의 넓은 섬은 가슴·어깨 판에 몰려 있다.
      // 쉼 자세 3D 셀 경계(F2-F1)로 가는 맥을 몸 전체(목·날개막·꼬리)에 깔고, 저주파 잡음 띠로 표면 절반 정도에만 흐르게 한다.
      // 텍스처가 이미 넓게 빛나는 섬 위에서는 맥을 빼서 가슴 쏠림을 더 키우지 않는다. 품질 low(detail 끔)는 텍스처만 쓴다
      let vein: THREE.Node<'float'> = float(0), veinCore: THREE.Node<'float'> = float(0), fine: THREE.Node<'float'> = float(0), halo: THREE.Node<'float'> = float(0), plate: THREE.Node<'float'> = float(0);
      if (this.useDetail) {
        // 셀 경계가 곧은 다각형(유리 금)으로 보이지 않게 잡음으로 좌표를 비튼다(r1: '깨진 유리 그물')
        const warp = vec3(mx_noise_float(pg.mul(0.9)), mx_noise_float(pg.mul(0.9).add(7.3)), mx_noise_float(pg.mul(0.9).add(13.1))).mul(0.35);
        const broad = smoothstep(0.42, 0.62, tex.a);
        // r3 블라인드('균일한 그물·금 간 유리', '폰에서 노이즈'): 두 단계로 나눈다.
        // 큰 균열 — 성긴 셀(약 1.3m) 경계, 굵고 밝은 주황~노랑 실선(r6에서 폭 1.35배를 시험했으나 블라인드 2명 중 1명 동률·1명 r5 우세 → r5 폭 유지). 몸 대부분을 지나지만 셀이 커서 그물로 읽히지 않는다
        const big = mx_worley_noise_vec2(pg.add(warp).mul(VEIN_FREQ * 0.55));
        const bigEdge = big.y.sub(big.x);
        const bigBand = smoothstep(-0.45, 0.0, mx_noise_float(pg.mul(0.21).add(vec3(3.1, 0.7, 5.3))));
        const keepBig = bigBand.mul(float(1).sub(broad));
        vein = float(1).sub(smoothstep(0.012, 0.05, bigEdge)).mul(keepBig);
        veinCore = float(1).sub(smoothstep(0.003, 0.018, bigEdge)).mul(keepBig);
        // 잔균열 — 촘촘한 셀 경계, 가늘고 어두운 주홍. 화면에서 한 픽셀보다 촘촘해지면(멀리·폰) 꺼서 노이즈로 깔리지 않게 한다
        const small = mx_worley_noise_vec2(pg.add(warp).mul(VEIN_FREQ));
        const smallEdge = small.y.sub(small.x);
        const smallBand = smoothstep(-0.1, 0.25, mx_noise_float(pg.mul(0.33).add(vec3(9.2, 1.4, 2.7))));
        const far = smoothstep(0.025, 0.07, fwidth(smallEdge));
        fine = float(1).sub(smoothstep(0.008, 0.035, smallEdge)).mul(smallBand).mul(float(1).sub(broad)).mul(float(1).sub(far));
        halo = max(float(1).sub(smoothstep(0.03, 0.16, bigEdge)).mul(keepBig), fine);
        // 비늘판마다 거칠기가 조금씩 다르게(흑요석 판은 반들, 골·맥은 거칠게)
        plate = mx_noise_float(pg.mul(2.3));
      }
      // 비늘(콘셉트 b-r2-wyrm: 검붉은 비늘 + 균열만 주황 발광). C8-6 r2 블라인드('갈색 플라스틱'·'머리가 어두워 얼굴이 안 읽힘'):
      // 맥 둘레를 어둡게 칠하던 것(0.45)은 머리를 뭉갰다 → 빼고, 대신 비늘 바탕을 조금 더 검게(흑요석) 둔다
      m.colorNode = tex.rgb.mul(vec3(0.8, 0.72, 0.76));
      // 마스크 문턱(L11): 알파 0.1~0.4가 비늘판·날개막 전체에 깔려 있어 몸 전체가 노랗게 빛났다(비평 '노란 기린').
      // 밝은 균열선·눈·입만 빛나게 문턱을 두고 세기를 올린다
      const crack = smoothstep(0.42, 0.85, tex.a);
      // C8-2: 가는 중심선(알파 0.8 위)만 한 번 더 세게 켜 블룸을 받게 한다
      const core = smoothstep(0.8, 0.95, tex.a);
      // 맥동은 몸을 따라 흐른다(꼬리→머리 방향 위상). 맥은 같은 맥동에 조금 늦게 따라온다
      const pulse = float(1).add(sin(this.uTime.mul(3.1).add(pg.z.mul(0.55))).mul(0.12));
      const veinPulse = float(1).add(sin(this.uTime.mul(2.2).add(pg.z.mul(0.9)).add(pg.y.mul(0.6))).mul(0.25));
      // 넓은 섬(가슴 판)의 세기를 낮추고(3.2 → CRACK_W) 가는 선·맥을 키워 발광을 몸 전체로 나눈다.
      // 격노·2페이즈·포효 솟음이 겹치면 uGlow가 3.8배까지 올라 HDR이 20을 넘었다(리뷰) → 곱한 세기를 8에서 자른다
      // 넓은 섬은 솟음에 1.6배까지만(r4 G: 가슴·몸 전체가 금빛), 가는 중심선·눈·입은 그대로 솟는다
      const texGlow = tex.rgb.mul(crack.mul(CRACK_W).mul(this.uGlow.min(1.6)).add(core.mul(4.0).mul(this.uGlow)).mul(pulse).min(8));
      // 맥 색: 가장자리는 짙은 주홍, 가운데 실선만 주황 노랑(밝은 주황을 넓게 세게 켜면 톤 매핑에서 연두빛 노랑으로 떴다 — r1).
      // 맥은 2페이즈·포효 솟음에 1.25배까지만 따라 오른다(r3 G: 솟음에서 몸 전체가 금빛 그물로 번져 형태가 사라짐). 솟음은 텍스처 균열이 맡는다
      const veinCol = mix(vec3(1.0, 0.14, 0.02), vec3(1.0, 0.34, 0.06), veinCore);
      const veinGain = this.uGlow.min(1.25).mul(veinPulse);
      const veinGlow = veinCol.mul(vein.mul(VEIN_W).mul(veinGain).min(4)).add(vec3(0.9, 0.1, 0.015).mul(fine.mul(FINE_W).mul(veinGain)));
      glowNode = texGlow.add(veinGlow).mul(this.uGlowTint);
      // 흑요석: 판은 반들(0.5), 균열·맥 둘레는 거칠게(0.9), 판마다 ±0.12. r3·r4: 0.34는 용암빛을 거울처럼 받아 몸 전체가 금속 금빛으로 읽혔다
      if (this.useDetail) m.roughnessNode = mix(float(0.5).add(plate.mul(0.12)), float(0.9), max(crack, halo)).clamp(0.22, 0.95);
    } else {
      m.color.setHex(0x3a2622);
    }
    // 피격: 몸 전체를 하얗게 바래지 않게 가장자리(프레넬)에만 잉걸불 빛. 파티가 쉬지 않고 치므로 약하게.
    // C8 회차2 비평(B 4.5점, '크림색 탈색·흰 천 날개'): 흰빛·넓은 프레넬(2.2)이 얇은 목·날개막을 통째로 덮었다 → 붉은 주황·좁게(4)
    const hitNode = vec3(1.25, 0.36, 0.08).mul(this.uHit).mul(ndv.pow(4));
    m.emissiveNode = materialEmissive.add(this.rim.color.mul(fres).mul(this.rim.strength)).add(glowNode).add(hitNode);
    // 영웅이 몸 뒤로 들어가면 성긴 망점(2×2 칸 중 1칸)만 비운다. 영웅은 자기 엑스레이 실루엣(kael.ts)으로 이미 보이므로
    // C8-2: 예전 1px 체크무늬(절반 비움)는 공격 예고·폰 장면에서 보스 전체를 망점 덩어리로 지워 실루엣이 사라졌다(비평 P1)
    const checker = float(1).sub(floor(screenCoordinate.x).mod(2).mul(floor(screenCoordinate.y).mod(2)));
    // C8-5(블라인드 r4 폰 F 4.0 '보스 전체가 망점으로 비쳐 실루엣이 깨짐'): 비우는 범위를 바닥~4.5m(영웅 키 + 여유)로 줄인다.
    // 영웅을 가리는 건 아래 몸통·다리·꼬리뿐이고, 머리·날개·상체는 그대로 둬야 보스 실루엣이 산다
    const low = float(1).sub(smoothstep(3.5, 5, positionWorld.y));
    for (const mt of [m, this.hullMat] as THREE.NodeMaterial[]) { mt.opacityNode = mix(float(1), checker, this.fade.mul(low)); mt.alphaTest = 0.5; }
    srcMat.dispose();
    sm.material = m;
    sm.castShadow = true;
    sm.frustumCulled = false;
    // 외곽선: 같은 골격에 묶은 반전 헐
    const hull = new THREE.SkinnedMesh(sm.geometry, this.hullMat);
    hull.position.copy(sm.position); hull.quaternion.copy(sm.quaternion); hull.scale.copy(sm.scale);
    hull.bind(sm.skeleton, sm.bindMatrix);
    hull.frustumCulled = false;
    sm.parent?.add(hull);
    this.mats.push(m);
    if (map) this.disposables.push(map);
    if (normalMap) this.disposables.push(normalMap);
    this.geos.push(sm.geometry);
    this.skinMat = m;
    this.mesh = sm;
    this.mouthBone = scene.getObjectByName('Mouth') ?? null;
    this.headBone = scene.getObjectByName('Head') ?? null;
    // C8-5(블라인드 P1 '머리·뿔 존재감 약함', 콘셉트 b-r2-wyrm은 큰 머리·가시 뿔이 화면을 지배): 머리 뼈만 키운다.
    // 클립에 Head 크기 트랙이 없어 한 번 정하면 유지된다. 턱·입 뼈가 자식이라 불 뿜는 위치도 같이 따라간다
    if (this.headBone) this.headBone.scale.setScalar(WYVERN_HEAD_SCALE);
    if (this.mouthBone) this.mouthBone.add(this.mouthRig);
    this.mixer = new THREE.AnimationMixer(scene);
    for (const clip of clips) {
      const i = CLIP_INDEX[clip.name as WyvernClip];
      if (i === undefined) continue;
      const a = this.mixer.clipAction(clip);
      a.setLoop(LOOP.has(clip.name as WyvernClip) ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
      a.clampWhenFinished = true;
      a.timeScale = 0;
      a.play();
      a.setEffectiveWeight(0);
      a.enabled = false;
      this.actions[i] = a;
    }
    this.model.add(scene);
    this.dedicated = true;
    this.inited = false;
  }

  /** 쓰고 있는 클립 무게(QA·테스트) */
  weightOf(clip: WyvernClip): number { return this.weights[CLIP_INDEX[clip]]; }
  get clipCount(): number { return this.actions.filter((a) => a).length; }

  reset(): void {
    this.inited = false; this.lastPat = -1; this.lastState = -1;
    this.roarT = -1; this.surge = 0; this.wasPhase2 = false; this.uHit.value = 0;
    this.weights.fill(0); this.loopTime.fill(0);
    this.flame.visible = false; this.throat.visible = false;
    this.proc.reset();
  }

  /** 입 위치(월드). 보이지 않으면 false */
  mouthWorld(out: THREE.Vector3): boolean {
    if (!this.root.visible) return false;
    if (!this.dedicated || !this.mouthBone) return this.proc.mouthWorld(out);
    this.mouthBone.getWorldPosition(out);
    return true;
  }

  /** 입이 향한 방향(월드, 단위 벡터): 머리 뼈 → 입 뼈 */
  mouthDir(out: THREE.Vector3): boolean {
    if (!this.root.visible) return false;
    if (!this.dedicated || !this.mouthBone || !this.headBone) return this.proc.mouthDir(out);
    this.headBone.getWorldPosition(this.tmpA);
    this.mouthBone.getWorldPosition(out);
    out.sub(this.tmpA).normalize();
    return true;
  }

  update(view: GameView, alpha: number, dt: number, clocks: GameClocks): void {
    const b = view.boss, st = b.state;
    const show = st !== BossState.Inactive && st !== BossState.Dead;
    this.root.visible = show;
    if (!show) { this.reset(); setCutawayBossLane(BOSS_LANE_DEFAULT); return; }
    // 비룡은 1.4배 모델에 날개를 반쯤 펴므로 보스 시선 통로를 넓힌다(다른 보스 방은 기본 폭 그대로)
    setCutawayBossLane(WYVERN_LANE);
    this.proc.breathCone = this.breathCone;
    if (!this.dedicated) {
      this.model.visible = false;
      this.proc.root.visible = true;
      this.proc.update(view, alpha, dt, clocks);
      this.x = this.proc.x; this.z = this.proc.z; this.height = this.proc.height;
      return;
    }
    this.proc.root.visible = false;
    this.model.visible = true;
    const r = view.raid;
    const air = typeof b.air === 'number' ? b.air : 0;
    const pat = r ? r.pattern : -1;
    if (st !== this.lastState) {
      // 친 뒤 상태로 넘어갈 때만 패턴 기억을 이어 간다(쫓기·경직에서 지운다)
      if (st === BossState.Chase || st === BossState.Stagger) this.lastPat = -1;
      this.lastState = st;
    }
    if (pat >= 0 && pat !== WyvernPattern.phase2 && pat !== WyvernPattern.enrage) this.lastPat = pat;
    const t = phaseOf(b.stateTick, clocks.worldRate, alpha);
    // 2페이즈 전환: 게임은 상태를 바꾸지 않으므로 그림만 포효(쫓기·대기 중일 때)와 발광 솟음을 더한다
    const p2 = b.phase2 === true;
    // C8-6 고정 프레임 픽스처: 솟음은 장면 값 그대로, 페이즈 전환 포효는 걸지 않는다
    const qf = view.qaFrame ?? null;
    if (qf) { this.wasPhase2 = p2; this.roarT = -1; this.surge = qf.surge ?? 0; }
    if (p2 && !this.wasPhase2 && this.inited) { this.roarT = 0; this.surge = 1; }
    this.wasPhase2 = p2;
    if (this.roarT >= 0) { this.roarT += clocks.worldDelta / 60; if (this.roarT > PHASE_ROAR_S) this.roarT = -1; }
    const tx = b.prevX + (b.x - b.prevX) * alpha, tz = b.prevZ + (b.z - b.prevZ) * alpha;
    const speed = clocks.worldRate > 0 ? Math.hypot(b.x - b.prevX, b.z - b.prevZ) * 60 / clocks.worldRate : 0;
    const pick = pickClip(this.pick, st, t, this.lastPat, air, speed > 0.05);
    if (this.roarT >= 0 && st === BossState.Chase && air < 0.02) setPick(pick, 'roar', this.roarT);
    if (!this.inited) {
      this.inited = true;
      this.sx = tx; this.sz = tz; this.yaw = b.facing;
      this.weights.fill(0); this.weights[CLIP_INDEX[pick.clip]] = 1;
      this.h = this.targetHeight(st, t, air);
    }
    // 위치·방향: 공중에서는 게임 위치가 브레스 줄 끝으로 뛰므로 미끄러지듯 따라간다(지상은 그대로)
    const follow = air > 0.02 ? damp(2.6, dt) : 1;
    this.sx += (tx - this.sx) * follow; this.sz += (tz - this.sz) * follow;
    this.yaw += angleDelta(this.yaw, b.facing) * (air > 0.02 ? damp(3, dt) : damp(14, dt));
    this.x = this.sx; this.z = this.sz;
    this.h += (this.targetHeight(st, t, air) - this.h) * damp(5, dt);
    this.height = this.h;
    this.root.position.set(this.sx, 0, this.sz);
    this.root.rotation.y = this.yaw;
    this.model.position.y = this.h;

    // 클립 무게·시간
    const target = CLIP_INDEX[pick.clip];
    const wdt = clocks.worldDelta / 60;
    // 한 번 치는 동작은 빨리(0.12초), 반복 동작 사이는 천천히(0.25초) 넘긴다
    const k = damp(LOOP.has(pick.clip) ? 7 : 14, dt);
    for (let i = 0; i < this.actions.length; i++) {
      const a = this.actions[i];
      this.weights[i] += ((i === target ? 1 : 0) - this.weights[i]) * k;
      if (!a) continue;
      const w = this.weights[i];
      a.enabled = w > 0.002;
      if (!a.enabled) continue;
      a.setEffectiveWeight(w);
      const name = WYVERN_CLIPS[i];
      const dur = a.getClip().duration;
      if (LOOP.has(name)) {
        const rate = name === 'walk' ? Math.min(2, Math.max(0.4, speed / WALK_SPEED)) : 1;
        // 고정 프레임에서는 반복 클립 시각을 세계 시계에서 바로 정한다(입장까지 걸린 시간에 따라 자세가 달라지지 않게)
        this.loopTime[i] = qf ? (clocks.worldNow / 60) % dur : (this.loopTime[i] + wdt * rate) % dur;
        a.time = this.loopTime[i];
      } else if (i === target) {
        a.time = Math.min(dur - 1e-3, Math.max(0, pick.time));
      }
    }
    this.mixer!.update(0);

    // 발광·목구멍·브레스
    let glow = (b.phase2 ? 1.25 : 0.85) * (r && r.enraged ? 1.45 : 1);
    if (st === BossState.Intro) glow *= 0.4 + 0.6 * clamp01(t / WYVERN.intro);
    if (st === BossState.Stagger) glow *= 0.6;
    if (st === BossState.Dying) glow *= 1 - 0.9 * clamp01(t / WYVERN.dying);
    let throat = 0, flame = 0;
    if (pick.clip === 'breath_air') { const bp = breathPhase(t, this.breath); throat = Math.max(bp.aim * 0.7, bp.fire); flame = bp.fire; }
    else if (st === BossState.Cast && this.lastPat === WyvernPattern.pools) throat = clamp01(t / WYVERN.pools.windup) ** 2;
    else if (st === BossState.Sweep && this.lastPat === WyvernPattern.pools) throat = 0.3 * (1 - clamp01(t / 20));
    if (!qf) this.surge = Math.max(0, this.surge - dt / 1.4);
    glow *= 1 + 1.1 * this.surge * this.surge;
    // 등장 포효: 몸 균열이 확 달아오른다
    if (st === BossState.Intro && pick.clip === 'roar') glow *= 1 + 0.3 * Math.max(0, 1 - pick.time / 1.2);
    this.glow += (glow - this.glow) * damp(9, dt);
    this.throatV += (throat - this.throatV) * damp(9, dt);
    this.flameV += (flame - this.flameV) * damp(18, dt);
    this.uGlow.value = this.glow;
    this.uThroat.value = this.throatV;
    this.uFlame.value = this.flameV;
    this.uTime.value = clocks.worldNow / 60;
    (this.uGlowTint.value as THREE.Color).setRGB(1, r && r.enraged ? 0.82 : 1, r && r.enraged ? 0.75 : 1);
    this.throat.visible = this.throatV > 0.02;
    this.flame.visible = this.breathCone && this.flameV > 0.02;
    if (this.mouthBone && (this.flame.visible || this.throat.visible)) {
      // 입 뼈 위치에서 머리→입 방향으로 원뿔을 돌린다
      this.root.updateMatrixWorld(true);
      this.mouthDir(this.tmpB);
      this.mouthBone.getWorldPosition(this.tmpA);
      this.mouthRig.lookAt(this.tmpA.add(this.tmpB));
      if (this.flame.visible) this.flame.scale.set(1 + this.flameV * 0.6, 1 + this.flameV * 0.6, 3 + 9 * this.flameV);
    }

    // 영웅이 몸 북쪽(카메라 반대편) 가까이 있어 가려지면 비춰 보이게 한다(공중이면 몸이 높아 가리지 않는다)
    const hv = view.hero;
    const hx = hv.prevX + (hv.x - hv.prevX) * alpha, hz = hv.prevZ + (hv.z - hv.prevZ) * alpha;
    const dz = this.sz - hz, dx = Math.abs(this.sx - hx);
    // C8-2: 영웅이 몸 아래(근접 거리, dz < 2.5)에 있을 때는 영웅 엑스레이 실루엣이 이미 보이므로 비룡을 비추지 않는다.
    // 예전 문턱(1.0m)은 근접전 내내 비룡 전체를 체크무늬로 바꿔 공격 예고 장면에서 보스가 사라졌다(r1 A 장면)
    const behind = air < 0.3 && dz > 2.5 && dz < 6.5 && dx < 3.6 ? 1 : 0;
    this.fadeV += (behind - this.fadeV) * damp(10, dt);
    this.fade.value = this.fadeV > 0.02 ? this.fadeV : 0;

    // 피격: 짧고 따뜻한 빛만 더한다(흰 플래시는 몸을 회색으로 바래게 했다, 2026-10-26). 공중·등장 무적이면 금빛 림 맥동
    const m = this.skinMat!;
    const hk = b.hitTick < HIT_FLASH_TICKS ? 1 - b.hitTick / HIT_FLASH_TICKS : 0;
    this.uHit.value = 0.45 * hk * hk;
    m.emissive.setHex(0x000000); m.emissiveIntensity = 0;
    if (b.invulnerable === true) {
      // C8: 금빛 림(최대 1.3)이 인트로 내내 몸 전체를 금색 덩어리로 바꿨다(비평 '황금 용'). 무적 신호는 잉걸불색 얇은 맥동으로만
      this.rim.color.value.setHex(0xff6a2a);
      this.rim.strength.value = 0.34 + 0.14 * Math.sin(clocks.realMs * 0.0088);
    } else {
      // C8: 비늘을 다시 칠한 뒤로 림은 실루엣만 떼는 어두운 붉은 테(0.3)로 낮춘다
      this.rim.color.value.setHex(r && r.enraged ? 0xff4020 : b.phase2 ? 0xe05a28 : 0xc8502a);
      // C8-2: 몸이 검붉은 배경 기둥에 묻혔다(비평) → 테를 조금 올려 실루엣을 떼어 낸다. C8-6 r3 블라인드도 같은 지적 → 0.42 → 0.52
      this.rim.strength.value = (r && r.enraged ? 0.66 + 0.15 * Math.sin(clocks.realMs * 0.006) : b.phase2 ? 0.6 : 0.52) + 0.4 * this.surge;
    }
  }

  /** 몸 높이 목표(m): 등장 하강, 공중 비행(이륙·착지는 air가 정한다) */
  private targetHeight(st: number, t: number, air: number): number {
    if (st === BossState.Intro) {
      const down = clamp01(t / (WYVERN.intro * INTRO_DOWN));
      return FLY_H * 1.3 * (1 - easeOut(down));
    }
    if (st === BossState.Dying) return 0;
    if (air > 0) {
      // 이륙: 도약(0.9~1.3초) 전에는 땅에 머문다
      if (st === BossState.Cast && this.lastPat === WyvernPattern.takeoff) return FLY_H * clamp01((air * WYVERN.flight.takeoff / 60 - 1.0) / 1.6);
      return FLY_H * air;
    }
    return 0;
  }

  /** 미리 컴파일·QA용 정보 */
  get meshCount(): number { let n = 0; this.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) n++; }); return n; }

  dispose(): void {
    this.proc.dispose();
    this.mixer?.stopAllAction();
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    for (const t of this.disposables) t.dispose();
  }
}
